import { useQuery } from '@tanstack/react-query';
import { trashItems } from '../../api/trash.ts';
import type { TrashItem } from '../../api/types.ts';
import { formatBytes, formatDateTime } from '../../lib/format.ts';
import { FileIcon } from '../files/FileIcon.tsx';
import { IconChevronDown, IconChevronRight } from '../../lib/icons.tsx';

interface Props {
  item: TrashItem;
  namespaceId: string;
  columns: string;
  checked: boolean;
  onToggle: (checked: boolean) => void;
  expanded: boolean;
  onExpand: () => void;
}

/**
 * 휴지통 한 줄.
 *
 * 폴더를 지우면 그 안의 파일이 한 묶음으로 들어온다. 목록에는 "N개" 라고만
 * 나와서, 무엇이 들어 있는지 모르고 복원하거나 영구 삭제해야 했다.
 * 펼쳐서 내용을 볼 수 있게 한다 — 서버에 API 는 있었지만 화면이 쓰지 않았다.
 */
export function TrashRow({
  item, namespaceId, columns, checked, onToggle, expanded, onExpand,
}: Props) {
  const hasChildren = item.item_count > 1;

  // 펼칠 때만 불러온다. 목록을 열자마자 전부 조회하면 항목이 많을 때 느리다.
  const contents = useQuery({
    queryKey: ['trash-items', namespaceId, item.id],
    queryFn: () => trashItems(namespaceId, item.id),
    enabled: expanded && hasChildren,
  });

  return (
    <>
      <div className="frow" style={{ gridTemplateColumns: columns }} data-selected={checked}>
        <input
          className="checkbox" type="checkbox" checked={checked}
          aria-label={`${item.name} 선택`}
          onChange={(e) => { onToggle(e.target.checked); }}
        />
        <div className="frow__name">
          {hasChildren ? (
            <button
              className="btn btn--ghost btn--icon trash__toggle"
              aria-label={expanded ? '내용 접기' : '내용 보기'}
              aria-expanded={expanded}
              onClick={onExpand}
            >
              {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
            </button>
          ) : (
            <span className="trash__toggle" />
          )}
          <FileIcon node={{ type: item.type, name: item.name, mime: null }} />
          <span className="frow__label">{item.name}</span>
          {hasChildren && <span className="frow__badge">{item.item_count}개</span>}
        </div>
        <div className="frow__meta truncate" title={item.original_path}>
          {item.original_path}
        </div>
        <div className="frow__meta">{formatBytes(item.total_size)}</div>
        <div className="frow__meta" title={formatDateTime(item.deleted_at)}>
          <span style={{ color: item.days_left <= 3 ? 'var(--c-danger)' : undefined }}>
            {item.days_left}일 남음
          </span>
        </div>
      </div>

      {expanded && hasChildren && (
        <div className="trash__contents">
          {contents.isLoading && <span className="spinner" />}
          {contents.error && (
            <p className="error-text">{(contents.error as Error).message}</p>
          )}
          {(contents.data?.items ?? []).map((child) => (
            <div key={child.path} className="trash__child">
              <FileIcon
                node={{ type: child.type as 'file' | 'folder', name: child.path, mime: null }}
              />
              <span className="truncate" title={child.path}>{child.path}</span>
              <span className="faint">
                {child.type === 'folder' ? '폴더' : formatBytes(child.size)}
              </span>
            </div>
          ))}
          {contents.data?.items.length === 0 && (
            <p className="muted" style={{ margin: 0 }}>내용이 없습니다.</p>
          )}
        </div>
      )}
    </>
  );
}
